const PMR171_HEADER = [0xa5, 0xa5, 0xa5, 0xa5] as const;

export const PMR171_COMMAND = {
  PTT: 0x07,
  FREQUENCY: 0x09,
  MODE: 0x0a,
  STATUS: 0x0b,
  BAND: 0x1d,
  POWER_LEVEL: 0x28,
  HIGH_LOW_POWER: 0x2c,
  PARAM_SYNC: 0x2e,
  USB_DATA_FORMAT: 0x33,
  CHANNEL_WRITE: 0x40,
  CHANNEL_READ: 0x41,
  CHANNEL_MODE: 0x42,
  DMR_CHANNEL_WRITE: 0x43,
  DMR_CHANNEL_READ: 0x44,
  FACTORY_IQ: 0x55,
  FACTORY_PA_BIAS: 0x56,
  FACTORY_RD16: 0x57,
  FACTORY_FWD_POWER: 0x58,
  FACTORY_SWR: 0x59,
  FACTORY_TX_FREQ: 0x60,
  FACTORY_RX_FREQ: 0x61,
  FACTORY_S_METER: 0x62,
  FACTORY_VOLTAGE: 0x63,
  FACTORY_TX_IQ_BALANCE: 0x64,
  FACTORY_MODE: 0x65,
  FACTORY_IQ_PHASE: 0x6f,
  FACTORY_LO_PHASE: 0x70,
  FACTORY_LO_AMPLITUDE: 0x72,
  DEVICE_ID: 0x66,
  TONE_TX: 0x6d,
  NETWORK: 0x6e,
  CDC_RTS_PTT: 0x71,
} as const;

/** 协议 §40 应答字段顺序，与后端 ParamSync0x2EParser 一致 */
export const PARAM_SYNC_FIELD_ORDER = [
  'svol',
  'hvol',
  'mic',
  'cmp',
  'bas',
  'trb',
  'rfg',
  'ifg',
  'sql',
  'agc',
  'amp',
  'nr',
  'nb',
  'peak',
  'span',
  'ref',
  'speed',
  'tCtss',
  'rCtss',
  'lVoice',
  'lTime',
  'keyMode',
  'txRx',
  'traning',
  'stf',
  'stg',
  'keySpeed',
  'decode',
  'threshold',
  'dataFormat',
] as const;

export type ParamSyncField = (typeof PARAM_SYNC_FIELD_ORDER)[number];

/** 协议 §40 应答：TX_RX 为第 23 个参数字节（0-based 22） */
const PARAM_SYNC_TX_RX_INDEX = PARAM_SYNC_FIELD_ORDER.indexOf('txRx');

export type ParsedParamSync = Partial<Record<ParamSyncField, number>>;

export function parseParamSyncFromFrame(frame: Uint8Array): ParsedParamSync | null {
  if (frame.length < 8 || (frame[5] & 0xff) !== PMR171_COMMAND.PARAM_SYNC) {
    return null;
  }
  const crcReserve = 2;
  const available = frame.length - 6 - crcReserve;
  if (available < 1) return null;
  const params: ParsedParamSync = {};
  const count = Math.min(PARAM_SYNC_FIELD_ORDER.length, available);
  for (let i = 0; i < count; i += 1) {
    params[PARAM_SYNC_FIELD_ORDER[i]] = frame[6 + i] & 0xff;
  }
  return params;
}

export function parseParamSyncFromRxHex(
  rxHex: string | null | undefined,
): ParsedParamSync | null {
  if (!rxHex) return null;
  try {
    return parseParamSyncFromFrame(parseHexBytes(rxHex));
  } catch {
    return null;
  }
}

export type ParsedPttState = 'receive' | 'transmit';

export const PMR171_VFO_MODES = [
  'USB',
  'LSB',
  'CWR',
  'CWL',
  'AM',
  'WFM',
  'NFM',
  'DIGI',
  'FT8',
  'DMR',
] as const;

export type Pmr171VfoMode = (typeof PMR171_VFO_MODES)[number];

export interface ParsedStatusSync {
  ptt: ParsedPttState | null;
  vfoAMode: Pmr171VfoMode | null;
  vfoBMode: Pmr171VfoMode | null;
  vfoAFrequency: number;
  vfoBFrequency: number;
  abBand: number | null;
  nr: number | null;
  nb: number | null;
  /** 状态栏 Bit5：0 低 / 1 高；帧过短则为 null */
  highLow: 0 | 1 | null;
}

function readBe32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] & 0xff) << 24) |
    ((bytes[offset + 1] & 0xff) << 16) |
    ((bytes[offset + 2] & 0xff) << 8) |
    (bytes[offset + 3] & 0xff)
  ) >>> 0;
}

function vfoModeName(code: number): Pmr171VfoMode | null {
  return PMR171_VFO_MODES[code] ?? null;
}

function writeBe32(value: number): number[] {
  const hz = Math.round(value) >>> 0;
  return [(hz >> 24) & 0xff, (hz >> 16) & 0xff, (hz >> 8) & 0xff, hz & 0xff];
}

export function vfoModeCode(mode: string | number | null | undefined): number {
  if (typeof mode === 'number' && Number.isInteger(mode) && mode >= 0 && mode <= 9) {
    return mode;
  }
  const raw = String(mode ?? 'USB').trim();
  if (/^\d+$/.test(raw)) {
    const numeric = Number(raw);
    if (numeric >= 0 && numeric <= 9) return numeric;
  }
  const name = raw.toUpperCase();
  if (name === 'DIG') return 7;
  if (name === 'PKT') return 8;
  const index = (PMR171_VFO_MODES as readonly string[]).indexOf(name);
  return index >= 0 ? index : 0;
}

export function buildFrequencySetFrame(vfoAHz: number, vfoBHz: number): Uint8Array {
  if (!Number.isFinite(vfoAHz) || vfoAHz < 0 || vfoAHz > 2_000_000_000) {
    throw new Error(`VFOA 频率超出范围: ${vfoAHz}`);
  }
  if (!Number.isFinite(vfoBHz) || vfoBHz < 0 || vfoBHz > 2_000_000_000) {
    throw new Error(`VFOB 频率超出范围: ${vfoBHz}`);
  }
  return buildPmr171Frame([
    PMR171_COMMAND.FREQUENCY,
    ...writeBe32(vfoAHz),
    ...writeBe32(vfoBHz),
  ]);
}

export function buildModeSetFrame(
  vfoAMode: string | number,
  vfoBMode: string | number,
): Uint8Array {
  return buildPmr171Frame([
    PMR171_COMMAND.MODE,
    vfoModeCode(vfoAMode),
    vfoModeCode(vfoBMode),
  ]);
}

function firstNumericValue(data: Record<string, unknown>): number | null {
  for (const value of Object.values(data)) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return Number(value);
  }
  return null;
}

function readDataInt(data: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return Number(value);
  }
  return null;
}

function readModeArg(value: unknown, fallback = 'USB'): string | number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) return value;
  return fallback;
}

function parseIpv4Octets(raw: unknown): number[] | null {
  if (typeof raw !== 'string') return null;
  const parts = raw.trim().split('.');
  if (parts.length !== 4) return null;
  const octets = parts.map(part => Number(part));
  if (octets.some(octet => !Number.isInteger(octet) || octet < 0 || octet > 255)) return null;
  return octets;
}

function parsePortValue(raw: unknown): number | null {
  const port = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isInteger(port) || port < 1 || port > 65535) return null;
  return port;
}

export function framesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if ((left[i] & 0xff) !== (right[i] & 0xff)) return false;
  }
  return true;
}

function isFactoryCalibrationCommand(command: number): boolean {
  const code = command & 0xff;
  return (
    (code >= PMR171_COMMAND.FACTORY_IQ && code <= PMR171_COMMAND.FACTORY_TX_IQ_BALANCE)
    || code === PMR171_COMMAND.FACTORY_IQ_PHASE
    || code === PMR171_COMMAND.FACTORY_LO_PHASE
    || code === PMR171_COMMAND.FACTORY_LO_AMPLITUDE
  );
}

export function encodeLocalControlCommand(
  typeCode: number,
  data: Record<string, unknown> = {},
): Uint8Array {
  const command = typeCode & 0xff;
  if (command === PMR171_COMMAND.PTT) {
    return buildPttFrame(Number(data.ptt) === 0);
  }
  if (command === PMR171_COMMAND.FREQUENCY) {
    return buildFrequencySetFrame(Number(data.vfoAFrequency), Number(data.vfoBFrequency));
  }
  if (command === PMR171_COMMAND.MODE) {
    return buildModeSetFrame(readModeArg(data.vfoAMode), readModeArg(data.vfoBMode));
  }
  if (command === PMR171_COMMAND.STATUS) {
    return buildPmr171Frame([PMR171_COMMAND.STATUS]);
  }
  if (command === PMR171_COMMAND.PARAM_SYNC) {
    return buildPmr171Frame([PMR171_COMMAND.PARAM_SYNC, 0x00]);
  }
  if (command === PMR171_COMMAND.DEVICE_ID) {
    return buildPmr171Frame([PMR171_COMMAND.DEVICE_ID]);
  }
  if (isFactoryCalibrationCommand(command)) {
    const rw = readDataInt(data, 'rw', 'readWrite') ?? 0;
    if (rw !== 0 && rw !== 1) {
      throw new Error('data.rw 必须为 0 或 1');
    }
    const value = rw === 0 ? 0 : readDataInt(data, 'value', 'calibration') ?? 0;
    if (value < 0 || value > 255) {
      throw new Error(`命令 0x${command.toString(16)} 校准值超出 0–255`);
    }
    return buildPmr171Frame([command, rw, value]);
  }
  if (command === PMR171_COMMAND.CDC_RTS_PTT) {
    const rw = readDataInt(data, 'rw', 'readWrite') ?? 0;
    if (rw === 0) return buildPmr171Frame([PMR171_COMMAND.CDC_RTS_PTT, 0x00]);
    if (rw !== 1) {
      throw new Error('data.rw 必须为 0 或 1');
    }
    const enabled = readDataInt(data, 'enabled', 'value');
    if (enabled !== 0 && enabled !== 1) {
      throw new Error('data.enabled 必须为 0 或 1');
    }
    return buildPmr171Frame([PMR171_COMMAND.CDC_RTS_PTT, 0x01, enabled]);
  }
  if (command === PMR171_COMMAND.FACTORY_MODE || command === PMR171_COMMAND.TONE_TX) {
    const enabled = readDataInt(data, 'enabled', 'value');
    if (enabled !== 0 && enabled !== 1) {
      throw new Error('data.enabled 必须为 0 或 1');
    }
    return buildPmr171Frame([command, enabled]);
  }
  if (command === PMR171_COMMAND.NETWORK) {
    const rw = readDataInt(data, 'rw', 'readWrite') ?? 0;
    if (rw === 0) return buildPmr171Frame([PMR171_COMMAND.NETWORK, 0x00]);
    const localIp = parseIpv4Octets(data.localIp);
    const netmask = parseIpv4Octets(data.netmask);
    const gateway = parseIpv4Octets(data.gateway);
    const remoteIp = parseIpv4Octets(data.remoteIp);
    const tcpPort = parsePortValue(data.tcpPort);
    const udpPort = parsePortValue(data.udpPort);
    if (!localIp || !netmask || !gateway || !remoteIp || tcpPort == null || udpPort == null) {
      throw new Error('0x6E 写入需要完整 IPv4 与端口');
    }
    return buildPmr171Frame([
      PMR171_COMMAND.NETWORK,
      0x01,
      ...localIp,
      ...netmask,
      ...gateway,
      ...remoteIp,
      tcpPort & 0xff,
      (tcpPort >> 8) & 0xff,
      udpPort & 0xff,
      (udpPort >> 8) & 0xff,
    ]);
  }
  if (
    command === PMR171_COMMAND.CHANNEL_WRITE ||
    command === PMR171_COMMAND.CHANNEL_READ ||
    command === PMR171_COMMAND.CHANNEL_MODE ||
    command === PMR171_COMMAND.DMR_CHANNEL_WRITE ||
    command === PMR171_COMMAND.DMR_CHANNEL_READ
  ) {
    throw new Error(`命令 0x${command.toString(16).padStart(2, '0')} 请使用信道编解码组帧`);
  }
  const value = firstNumericValue(data);
  if (value == null) {
    throw new Error(`命令 0x${command.toString(16).padStart(2, '0')} 缺少数据`);
  }
  if (value < 0 || value > 255) {
    throw new Error(`命令 0x${command.toString(16).padStart(2, '0')} 数据超出 0–255`);
  }
  return buildPmr171Frame([command, value]);
}

/** 协议 §5 0x0B：收发状态、VFOA/B 模式与频率（大端 Hz） */
export function parseStatusSyncFromFrame(frame: Uint8Array): ParsedStatusSync | null {
  if (frame.length < 19 || (frame[5] & 0xff) !== PMR171_COMMAND.STATUS) {
    return null;
  }
  const vfoAFrequency = readBe32(frame, 9);
  const vfoBFrequency = readBe32(frame, 13);
  if (vfoAFrequency > 2_000_000_000 || vfoBFrequency > 2_000_000_000) {
    return null;
  }
  const hasAbNr = frame.length >= 21;
  const nrNb = hasAbNr ? parseNrNb(frame[18] & 0xff) : { nr: null, nb: null };
  const abCode = hasAbNr ? frame[17] & 0xff : -1;
  const statusBarOffset = 27;
  const highLow =
    frame.length > statusBarOffset
      ? ((frame[statusBarOffset] & 0x20) !== 0 ? 1 : 0)
      : null;
  return {
    ptt: pttStateFromCode(frame[6] & 0xff),
    vfoAMode: vfoModeName(frame[7] & 0xff),
    vfoBMode: vfoModeName(frame[8] & 0xff),
    vfoAFrequency,
    vfoBFrequency,
    abBand: abCode === 0 || abCode === 1 || abCode === 2 ? abCode : null,
    nr: nrNb.nr,
    nb: nrNb.nb,
    highLow,
  };
}

function parseNrNb(code: number): { nr: number | null; nb: number | null } {
  if (code === 0) return { nr: 0, nb: 0 };
  if (code === 1) return { nr: 1, nb: 0 };
  if (code === 2) return { nr: 0, nb: 1 };
  return { nr: null, nb: null };
}

export function isFrequencySetAck(frame: ParsedPmr171Frame): boolean {
  if (!frame.crcValid) return false;
  if ((frame.command & 0xff) === PMR171_COMMAND.FREQUENCY) return true;
  return parseStatusSyncFromFrame(frame.raw) != null;
}

export function parseStatusSyncFromRxHex(
  rxHex: string | null | undefined,
): ParsedStatusSync | null {
  if (!rxHex) return null;
  try {
    return parseStatusSyncFromFrame(parseHexBytes(rxHex));
  } catch {
    return null;
  }
}

export function parseFrequencySetFromRxHex(
  rxHex: string | null | undefined,
): { vfoAFrequency: number; vfoBFrequency: number } | null {
  if (!rxHex) return null;
  try {
    const { frames } = extractPmr171Frames(parseHexBytes(rxHex));
    const frame = [...frames]
      .reverse()
      .find(item => (item.command & 0xff) === PMR171_COMMAND.FREQUENCY && item.crcValid);
    if (!frame || frame.data.length < 8) return null;
    const vfoAFrequency =
      ((frame.data[0] & 0xff) << 24) |
      ((frame.data[1] & 0xff) << 16) |
      ((frame.data[2] & 0xff) << 8) |
      (frame.data[3] & 0xff);
    const vfoBFrequency =
      ((frame.data[4] & 0xff) << 24) |
      ((frame.data[5] & 0xff) << 16) |
      ((frame.data[6] & 0xff) << 8) |
      (frame.data[7] & 0xff);
    return { vfoAFrequency: vfoAFrequency >>> 0, vfoBFrequency: vfoBFrequency >>> 0 };
  } catch {
    return null;
  }
}

export function parsePttStateFromFrame(frame: Uint8Array): ParsedPttState | null {
  if (frame.length < 7) return null;
  const command = frame[5] & 0xff;
  if (command === PMR171_COMMAND.STATUS) {
    return pttStateFromCode(frame[6] & 0xff);
  }
  if (command === PMR171_COMMAND.PARAM_SYNC) {
    const offset = 6 + PARAM_SYNC_TX_RX_INDEX;
    if (frame.length <= offset) return null;
    return pttStateFromCode(frame[offset] & 0xff);
  }
  return null;
}

export function parsePttStateFromRxHex(rxHex: string | null | undefined): ParsedPttState | null {
  if (!rxHex) return null;
  try {
    return parsePttStateFromFrame(parseHexBytes(rxHex));
  } catch {
    return null;
  }
}

function pttStateFromCode(code: number): ParsedPttState | null {
  if (code === 0) return 'receive';
  if (code === 1) return 'transmit';
  return null;
}

export interface ParsedPmr171Frame {
  raw: Uint8Array;
  command: number;
  data: Uint8Array;
  crcValid: boolean;
}

export interface ExtractedPmr171Frames {
  frames: ParsedPmr171Frame[];
  remaining: Uint8Array;
}

export function crc16CcittFalse(data: ArrayLike<number>): number {
  let crc = 0xffff;

  for (let i = 0; i < data.length; i += 1) {
    crc ^= (data[i] & 0xff) << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }

  return crc;
}

/**
 * data 的第一个字节为命令码。包长包含命令/数据与末尾两个 CRC 字节。
 */
export function buildPmr171Frame(data: ArrayLike<number>): Uint8Array {
  if (data.length < 1 || data.length > 253) {
    throw new Error('PMR-171 数据长度必须在 1–253 字节之间');
  }

  const packetLength = data.length + 2;
  const crcInput = new Uint8Array(1 + data.length);
  crcInput[0] = packetLength;
  for (let i = 0; i < data.length; i += 1) {
    crcInput[i + 1] = data[i] & 0xff;
  }
  const crc = crc16CcittFalse(crcInput);

  return new Uint8Array([
    ...PMR171_HEADER,
    packetLength,
    ...Array.from(data, value => value & 0xff),
    (crc >> 8) & 0xff,
    crc & 0xff,
  ]);
}

export function buildPttFrame(pressed: boolean): Uint8Array {
  return buildPmr171Frame([PMR171_COMMAND.PTT, pressed ? 0x00 : 0x01]);
}

export function parseHexBytes(input: string): Uint8Array {
  const normalized = input
    .replace(/0x/gi, '')
    .replace(/[\s,;:_-]+/g, '')
    .trim();

  if (!normalized) {
    throw new Error('请输入十六进制数据');
  }
  if (!/^[0-9a-f]+$/i.test(normalized)) {
    throw new Error('十六进制数据包含无效字符');
  }
  if (normalized.length % 2 !== 0) {
    throw new Error('十六进制数据必须由完整字节组成');
  }

  const bytes = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export function bytesToHex(data: ArrayLike<number>): string {
  return Array.from(data, value => (value & 0xff).toString(16).padStart(2, '0').toUpperCase()).join(' ');
}

export function concatBytes(left: Uint8Array, right: Uint8Array): Uint8Array {
  const result = new Uint8Array(left.length + right.length);
  result.set(left);
  result.set(right, left.length);
  return result;
}

/** 半截 TX 回波补齐前的静默等待，避免和第 10 字节竞态。 */
export const TRUNCATED_TX_ECHO_IDLE_MS = 60;

/**
 * 收包只少 lastTx 最后 1 字节（常见 CRC 低字节）时，补齐为完整回波。
 */
export function isPmr171TxPrefix(
  remaining: Uint8Array,
  lastTx: Uint8Array | null | undefined,
): boolean {
  if (!lastTx || remaining.length === 0 || remaining.length >= lastTx.length) return false;
  for (let i = 0; i < remaining.length; i += 1) {
    if ((remaining[i] & 0xff) !== (lastTx[i] & 0xff)) return false;
  }
  return true;
}

export function completeTruncatedTxEcho(
  remaining: Uint8Array,
  lastTx: Uint8Array | null | undefined,
): Uint8Array | null {
  if (!lastTx || lastTx.length < 8) return null;
  if (remaining.length !== lastTx.length - 1) return null;
  for (let i = 0; i < remaining.length; i += 1) {
    if ((remaining[i] & 0xff) !== (lastTx[i] & 0xff)) return null;
  }
  const completed = new Uint8Array(lastTx.length);
  completed.set(remaining);
  completed[lastTx.length - 1] = lastTx[lastTx.length - 1];
  return completed;
}

function findPmr171Sync(
  buffer: Uint8Array,
  offset: number,
): { header: number; syncLen: 3 | 4 } | null {
  for (let i = offset; i + 3 < buffer.length; i += 1) {
    if (
      buffer[i] === PMR171_HEADER[0] &&
      buffer[i + 1] === PMR171_HEADER[1] &&
      buffer[i + 2] === PMR171_HEADER[2] &&
      buffer[i + 3] === PMR171_HEADER[3]
    ) {
      return { header: i, syncLen: 4 };
    }
  }
  for (let i = offset; i + 2 < buffer.length; i += 1) {
    if (
      buffer[i] === PMR171_HEADER[0] &&
      buffer[i + 1] === PMR171_HEADER[1] &&
      buffer[i + 2] === PMR171_HEADER[2]
    ) {
      return { header: i, syncLen: 3 };
    }
  }
  return null;
}

export function extractPmr171Frames(buffer: Uint8Array): ExtractedPmr171Frames {
  const frames: ParsedPmr171Frame[] = [];
  let offset = 0;

  while (offset + 4 <= buffer.length) {
    const sync = findPmr171Sync(buffer, offset);
    if (!sync) {
      const keep = Math.min(3, buffer.length);
      return { frames, remaining: buffer.slice(buffer.length - keep) };
    }

    const { header, syncLen } = sync;
    const lengthOffset = header + syncLen;
    if (lengthOffset >= buffer.length) {
      return { frames, remaining: buffer.slice(header) };
    }

    const packetLength = buffer[lengthOffset];
    const commandOffset = lengthOffset + 1;
    // 频谱回复历史上包长固定为 0，实际总长为 520 字节。
    const totalLength =
      packetLength === 0 && syncLen === 4 && buffer[commandOffset] === 0x39
        ? 520
        : syncLen + 1 + packetLength;
    if (totalLength < syncLen + 4) {
      offset = header + 1;
      continue;
    }
    if (header + totalLength > buffer.length) {
      return { frames, remaining: buffer.slice(header) };
    }

    const wire = buffer.slice(header, header + totalLength);
    const crcOffset = wire.length - 2;
    const receivedCrc = (wire[crcOffset] << 8) | wire[crcOffset + 1];
    const calculatedCrc = crc16CcittFalse(wire.slice(syncLen, crcOffset));
    let raw = wire;
    if (syncLen === 3) {
      raw = new Uint8Array(wire.length + 1);
      raw[0] = PMR171_HEADER[0];
      raw.set(wire, 1);
    }
    const command = raw[5];
    const crcValid = command === 0x39 || receivedCrc === calculatedCrc;
    if (!crcValid) {
      offset = header + 1;
      continue;
    }
    frames.push({
      raw,
      command,
      data: raw.slice(6, raw.length - 2),
      crcValid: true,
    });
    offset = header + totalLength;
  }

  return { frames, remaining: buffer.slice(offset) };
}

export function parseDeviceIdentity(frame: ParsedPmr171Frame): {
  deviceType: number;
  serialNumber: number;
} | null {
  if (
    frame.command !== PMR171_COMMAND.DEVICE_ID ||
    frame.data.length < 5 ||
    !frame.crcValid
  ) {
    return null;
  }

  const deviceType = frame.data[0];
  // 与后端 CommandResponseManager 保持一致：
  // AMP-1KW（类型 8）使用小端序，其它设备（包括 PMR-171 类型 6）使用大端序。
  const serialNumber =
    deviceType === 8
      ? (frame.data[1] +
          frame.data[2] * 0x100 +
          frame.data[3] * 0x10000 +
          frame.data[4] * 0x1000000) >>>
        0
      : (frame.data[1] * 0x1000000 +
          frame.data[2] * 0x10000 +
          frame.data[3] * 0x100 +
          frame.data[4]) >>>
        0;
  return { deviceType, serialNumber };
}

/** 0x71 应答：数据区 [读写字, 使能值]。短读回波只有读写字，不当成功。 */
export function parseCdcRtsPttReply(frame: ParsedPmr171Frame): 0 | 1 | null {
  if ((frame.command & 0xff) !== PMR171_COMMAND.CDC_RTS_PTT || !frame.crcValid) {
    return null;
  }
  if (frame.data.length < 2) return null;
  const enabled = frame.data[1] & 0xff;
  if (enabled !== 0 && enabled !== 1) return null;
  return enabled as 0 | 1;
}
